import { Fragment } from 'react';
import { useFeatures } from '../hooks/useFeatures';
import type { Feature, FeatureCategory, FeatureEdition, GetLabel, ShowFeature, ShowLightbox } from '../types/feature';
import Heading from './Heading';
import Infobox from './Infobox';
import ListItem from './ListItem';
import Tile from './Tile';

type FeaturesProps = {
	categories: Record<string, FeatureCategory>;
	editions: FeatureEdition[];
	features: Feature[];
	categoryFilterTerm: string;
	getLabel: GetLabel;
	searchValue: string;
	showLightbox: ShowLightbox;
	showFeature?: ShowFeature;
	useListView: boolean;
	isFiltered: boolean;
};

export default function Features({
	categories,
	editions,
	features,
	categoryFilterTerm,
	getLabel,
	searchValue,
	showLightbox,
	showFeature,
	useListView,
	isFiltered,
}: FeaturesProps) {
	const [filteredPrimaryFeatures, filteredSecondaryFeatures] = useFeatures(features, categoryFilterTerm, searchValue);

	const groupByCategory = categoryFilterTerm === 'all' && searchValue === '' && !isFiltered && Object.keys(categories).length > 0;

	// The primary feature groups in render order, so the dialog navigation follows the visible order.
	const primaryGroups = groupByCategory
		? Object.entries(categories)
				.filter(([key]) => !['all', 'staff_pick'].includes(key))
				.map(([key, category]) => ({
					key,
					category,
					features: filteredPrimaryFeatures.filter((feature) => feature.primary_category === key),
				}))
		: [{ key: 'all', category: null, features: filteredPrimaryFeatures }];

	const orderedFeatures = [...primaryGroups.flatMap((group) => group.features), ...filteredSecondaryFeatures];

	const renderFeatures = (features: Feature[], offset: number) => {
		return useListView ? (
			<div className="feature-list">
				{features.map((feature, index) => (
					<ListItem key={index} feature={feature} categories={categories} getLabel={getLabel} editions={editions} showLightbox={showLightbox} />
				))}
			</div>
		) : (
			<div className="tile-grid">
				{features.map((feature, index) => (
					<Tile key={index} feature={feature} categories={categories} onClick={() => showFeature?.(orderedFeatures, offset + index)} />
				))}
			</div>
		);
	};

	const renderMainFeatures = () => {
		let offset = 0;

		return primaryGroups.map((group) => {
			const groupOffset = offset;
			offset += group.features.length;

			if (!group.category) {
				return <Fragment key={group.key}>{renderFeatures(group.features, groupOffset)}</Fragment>;
			}

			return (
				<Fragment key={group.key}>
					<Heading title={group.category.title} description={group.category.desc} type="h3" />
					{renderFeatures(group.features, groupOffset)}
				</Fragment>
			);
		});
	};

	return (
		<div className="feature__content">
			<div className="feature__content--primary">
				{filteredPrimaryFeatures.length ? renderMainFeatures() : <Infobox type="warning">{getLabel('no_entries_desc')}</Infobox>}
			</div>
			{filteredSecondaryFeatures.length > 0 && (
				<div className="feature__content--additional">
					<Heading title={getLabel('additional_title')} description={getLabel('additional_desc')} type="h3" />
					{renderFeatures(filteredSecondaryFeatures, orderedFeatures.length - filteredSecondaryFeatures.length)}
				</div>
			)}
		</div>
	);
}
